import { Add as AddIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { SubjectForm } from '../../lib/components/SubjectForm';
import { SubjectTable } from '../../lib/components/SubjectTable';
import { useCurriculumStore } from '../../stores/curriculumStore';
import type { Subject } from '../../types';

type DialogMode = 'create' | 'edit' | 'view' | null;

export const Subjects: React.FC = () => {
  const { subjects, addSubject, updateSubject, deleteSubject } = useCurriculumStore();

  const [dialogMode, setDialogMode] = useState<DialogMode>(null);
  const [selectedSubject, setSelectedSubject] = useState<Subject | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // Stats for dashboard cards
  const totalSubjects = subjects.length;
  const coreSubjects = subjects.filter((s) => s.category === 'core' || s.category === 'stem').length;
  const electiveSubjects = subjects.filter((s) => s.category === 'elective' || s.category === 'arts').length;

  const handleCreate = () => {
    setSelectedSubject(null);
    setDialogMode('create');
  };

  const handleEdit = (subject: Subject) => {
    setSelectedSubject(subject);
    setDialogMode('edit');
  };

  const handleView = (subject: Subject) => {
    setSelectedSubject(subject);
    setDialogMode('view');
  };

  const handleDelete = (subject: Subject) => {
    setSelectedSubject(subject);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    if (selectedSubject) {
      deleteSubject(selectedSubject.id);
      setDeleteConfirmOpen(false);
      setSelectedSubject(null);
      if (dialogMode === 'view') {
        setDialogMode(null);
      }
    }
  };

  const handleFormSubmit = (data: Subject) => {
    if (dialogMode === 'create') {
      const newSubject: Subject = {
        ...data,
        id: `subject-${Date.now()}`,
      };
      addSubject(newSubject);
    } else if (dialogMode === 'edit' && selectedSubject) {
      updateSubject(selectedSubject.id, data);
    }
    setDialogMode(null);
    setSelectedSubject(null);
  };

  const handleCloseDialog = () => {
    setDialogMode(null);
    setSelectedSubject(null);
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Subjects
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
            Add Subject
          </Button>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" color="text.secondary">
                Total Subjects
              </Typography>
              <Typography variant="h3">{totalSubjects}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" color="text.secondary">
                Core & STEM
              </Typography>
              <Typography variant="h3">{coreSubjects}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" color="text.secondary">
                Elective & Arts
              </Typography>
              <Typography variant="h3">{electiveSubjects}</Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Subjects Table */}
        <Paper sx={{ p: 2 }}>
          <SubjectTable subjects={subjects} onEdit={handleEdit} onView={handleView} onDelete={handleDelete} />
        </Paper>

        {/* Create/Edit Dialog */}
        <Dialog
          open={dialogMode === 'create' || dialogMode === 'edit'}
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>{dialogMode === 'create' ? 'Add New Subject' : 'Edit Subject'}</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <SubjectForm
                subject={selectedSubject || undefined}
                onSubmit={handleFormSubmit}
                onCancel={handleCloseDialog}
              />
            </Box>
          </DialogContent>
        </Dialog>

        {/* View Dialog */}
        <Dialog open={dialogMode === 'view'} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              Subject Details
              <Chip label={selectedSubject?.category || ''} size="small" />
            </Box>
          </DialogTitle>
          <DialogContent>
            {selectedSubject && (
              <Box sx={{ pt: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Name
                    </Typography>
                    <Typography variant="body1">{selectedSubject.name}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Code
                    </Typography>
                    <Typography variant="body1">{selectedSubject.code}</Typography>
                  </Grid>
                  {selectedSubject.examCode && (
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Exam Code
                      </Typography>
                      <Typography variant="body1">{selectedSubject.examCode}</Typography>
                    </Grid>
                  )}
                  {selectedSubject.department && (
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Department
                      </Typography>
                      <Typography variant="body1">{selectedSubject.department}</Typography>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Category
                    </Typography>
                    <Chip label={selectedSubject.category} size="small" sx={{ mt: 0.5 }} />
                  </Grid>
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Close</Button>
            <Button onClick={() => selectedSubject && handleEdit(selectedSubject)} variant="contained">
              Edit
            </Button>
            <Button onClick={() => selectedSubject && handleDelete(selectedSubject)} color="error">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              This will also remove all associated curriculum plans
            </Alert>
            <Typography>Are you sure you want to delete {selectedSubject?.name}?</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
            <Button onClick={handleConfirmDelete} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default Subjects;
